import { useState } from "react";
import { Mail, Phone, MessageCircle, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import Reveal from "./Reveal";
import { useT } from "../../i18n/I18nContext";
import { WHATSAPP_NUMBERS, EMAIL, PRIMARY_WHATSAPP } from "./contacts";

// Formspree endpoint — receives contact form submissions
const FORMSPREE_ENDPOINT = "https://formspree.io/f/xaqklrop";

export const Contact = () => {
  const t = useT();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    setLoading(true);
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        body: data,
        headers: { Accept: "application/json" },
      });

      if (res.ok) {
        toast.success(t.contact.successTitle, { description: t.contact.successDesc });
        setSubmitted(true);
        form.reset();
      } else {
        const body = await res.json().catch(() => ({}));
        const msg = body?.errors?.[0]?.message || t.contact.errorGeneric;
        toast.error(t.contact.errorTitle, { description: msg });
      }
    } catch (_err) {
      toast.error(t.contact.networkErrorTitle, { description: t.contact.networkErrorDesc });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 lg:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#2563EB]">
                {t.contact.eyebrow}
              </p>
            </Reveal>
            <Reveal delay={60}>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A2342] tracking-tight leading-[1.1]">
                {t.contact.title}
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <p className="mt-5 text-base lg:text-lg text-slate-600 leading-relaxed">
                {t.contact.body}
              </p>
            </Reveal>

            <Reveal delay={180}>
              <ul className="mt-10 space-y-5">
                <li className="flex items-start gap-4">
                  <span className="inline-flex w-11 h-11 shrink-0 items-center justify-center rounded-xl bg-[#2563EB]/10 text-[#2563EB]">
                    <Phone className="w-5 h-5" />
                  </span>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
                      {t.contact.phoneLabel}
                    </p>
                    <ul className="mt-1 space-y-0.5">
                      {WHATSAPP_NUMBERS.map((n, idx) => (
                        <li key={n.digits}>
                          <a
                            href={`https://wa.me/${n.digits}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`contact-phone-link-${idx}`}
                            className="inline-block text-base font-semibold text-[#0A2342] hover:text-[#2563EB] transition-colors"
                          >
                            {n.display}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="inline-flex w-11 h-11 shrink-0 items-center justify-center rounded-xl bg-[#2563EB]/10 text-[#2563EB]">
                    <Mail className="w-5 h-5" />
                  </span>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
                      {t.contact.emailLabel}
                    </p>
                    <a
                      href={`mailto:${EMAIL}`}
                      data-testid="contact-email-link"
                      className="mt-1 inline-block text-base font-semibold text-[#0A2342] hover:text-[#2563EB] transition-colors break-all"
                    >
                      {EMAIL}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="inline-flex w-11 h-11 shrink-0 items-center justify-center rounded-xl bg-[#2563EB]/10 text-[#2563EB]">
                    <MapPin className="w-5 h-5" />
                  </span>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
                      {t.contact.servingLabel}
                    </p>
                    <p className="mt-1 text-base font-semibold text-[#0A2342]">
                      {t.contact.servingValue}
                    </p>
                  </div>
                </li>
              </ul>
            </Reveal>

            <Reveal delay={240}>
              <div className="mt-10 flex flex-wrap gap-3">
                <a
                  href={`https://wa.me/${PRIMARY_WHATSAPP.digits}?text=${encodeURIComponent(
                    t.contact.whatsappPrefill
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-[#10B981] px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[#059669] transition-all"
                >
                  <MessageCircle className="w-4 h-4" />
                  {t.contact.whatsappCta}
                </a>
                <a
                  href={`mailto:${EMAIL}?subject=${encodeURIComponent(t.contact.emailSubject)}`}
                  data-testid="contact-mailto-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#0A2342] ring-1 ring-inset ring-slate-300 hover:bg-slate-50 transition-all"
                >
                  <Mail className="w-4 h-4" />
                  {t.contact.emailCta}
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={120}>
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="relative bg-[#F8FAFC] rounded-3xl border border-slate-200/80 p-7 sm:p-10 shadow-sm"
              >
                <div className="absolute -top-4 left-8 inline-flex items-center gap-2 rounded-full bg-[#0A2342] px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
                  {t.contact.formBadge}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm font-semibold text-[#0A2342]">
                      {t.contact.fields.name}
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      data-testid="contact-input-name"
                      placeholder={t.contact.fields.namePlaceholder}
                      className="h-12 bg-white border-slate-200 rounded-xl text-[15px] focus-visible:ring-[#2563EB]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="business" className="text-sm font-semibold text-[#0A2342]">
                      {t.contact.fields.business}
                    </Label>
                    <Input
                      id="business"
                      name="business"
                      type="text"
                      required
                      data-testid="contact-input-business"
                      placeholder={t.contact.fields.businessPlaceholder}
                      className="h-12 bg-white border-slate-200 rounded-xl text-[15px] focus-visible:ring-[#2563EB]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-semibold text-[#0A2342]">
                      {t.contact.fields.email}
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      data-testid="contact-input-email"
                      placeholder={t.contact.fields.emailPlaceholder}
                      className="h-12 bg-white border-slate-200 rounded-xl text-[15px] focus-visible:ring-[#2563EB]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-semibold text-[#0A2342]">
                      {t.contact.fields.phone}
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      data-testid="contact-input-phone"
                      placeholder={t.contact.fields.phonePlaceholder}
                      className="h-12 bg-white border-slate-200 rounded-xl text-[15px] focus-visible:ring-[#2563EB]"
                    />
                  </div>
                </div>

                <div className="mt-5 space-y-2">
                  <Label htmlFor="message" className="text-sm font-semibold text-[#0A2342]">
                    {t.contact.fields.message}
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    data-testid="contact-input-message"
                    placeholder={t.contact.fields.messagePlaceholder}
                    className="bg-white border-slate-200 rounded-xl text-[15px] resize-none focus-visible:ring-[#2563EB]"
                  />
                </div>

                <input
                  type="hidden"
                  name="_subject"
                  value="New website enquiry from UrbanVista Web site"
                />

                <button
                  type="submit"
                  disabled={loading || submitted}
                  data-testid="contact-submit"
                  className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2563EB] px-7 py-4 text-sm font-semibold text-white shadow-sm hover:bg-[#1D4ED8] disabled:opacity-70 disabled:cursor-not-allowed transition-all"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t.contact.submitting}
                    </>
                  ) : submitted ? (
                    t.contact.submitted
                  ) : (
                    <>
                      {t.contact.submit}
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                <p className="mt-4 text-xs text-slate-500 text-center">
                  {t.contact.privacyNote}
                </p>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
